import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { formatDateTime } from '../../utils/functions';
import { FeeBasis, Lead, Proposal, Triage } from '../../services/api/marketplaceService';
import { enumLabel } from './marketplaceLabels';

/** Section heading on the bare detail canvas (mirrors ContractDetail's). */
export const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <section className="space-y-3">
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-[11px] text-fg-muted">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const LEAD_STATUS_COLOR: Record<Lead['status'], BadgeColor> = {
  draft: 'gray',
  triaged: 'blue',
  sent: 'blue',
  proposed: 'amber',
  engaged: 'green',
  declined: 'gray',
  closed: 'gray',
};

const PROPOSAL_STATUS_COLOR: Record<Proposal['status'], BadgeColor> = {
  proposed: 'blue',
  countered: 'amber',
  accepted: 'green',
  declined: 'gray',
  withdrawn: 'gray',
};

const URGENCY_COLOR: Record<NonNullable<Triage['urgency']>, BadgeColor> = { low: 'gray', medium: 'amber', high: 'red' };

/**
 * A request's status in words its reader uses. The client follows their own
 * request ("Waiting for proposals"); a lawyer scans an inbox ("Open").
 */
const leadStatusLabel = (t: TFunction, status: Lead['status'], audience: 'client' | 'lawyer'): string => {
  const labels: Partial<Record<Lead['status'], string>> = audience === 'client'
    ? {
      draft: t('Draft'),
      triaged: t('Waiting for proposals'),
      sent: t('Waiting for proposals'),
      proposed: t('Proposals received'),
      engaged: t('Lawyer hired'),
      declined: t('Closed'),
      closed: t('Closed'),
    }
    : {
      triaged: t('Open'),
      sent: t('Open'),
      proposed: t('Proposals in'),
      engaged: t('Engaged'),
    };
  return labels[status] ?? enumLabel(t, status);
};

export const LeadStatusBadge: React.FC<{ status: Lead['status']; audience: 'client' | 'lawyer' }> = ({ status, audience }) => {
  const { t } = useTranslation();
  return <Badge color={LEAD_STATUS_COLOR[status] ?? 'gray'}>{leadStatusLabel(t, status, audience)}</Badge>;
};

/** "NO" → "Norway" in the viewer's language; no country yet reads as such. */
export const countryName = (t: TFunction, code: string | null | undefined): string => {
  const clean = String(code || '').trim().toUpperCase();
  if (!clean || clean === 'UNKNOWN') return t('Not determined yet');
  if (clean.length !== 2) return clean;
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(clean) || clean;
  } catch {
    return clean;
  }
};

/** Countries offered on intake: Norway (launch country) first, the rest by name. */
const INTAKE_COUNTRIES = ['NO', 'BG', 'DK', 'FI', 'FR', 'DE', 'IS', 'IE', 'IL', 'IT', 'NL', 'PL', 'PT', 'ES', 'SE', 'GB', 'US'];

export const intakeCountryOptions = (t: TFunction): { value: string; label: string }[] => {
  const [first, ...rest] = INTAKE_COUNTRIES.map((c) => ({ value: c, label: countryName(t, c) }));
  return [first, ...rest.sort((a, b) => a.label.localeCompare(b.label))];
};

/** Proposal currencies; the firm's own currency is added when it isn't listed. */
export const PROPOSAL_CURRENCIES = ['NOK', 'EUR', 'SEK', 'DKK', 'GBP', 'USD', 'ILS', 'BGN'];

export const ProposalStatusBadge: React.FC<{ status: Proposal['status'] }> = ({ status }) => {
  const { t } = useTranslation();
  return <Badge color={PROPOSAL_STATUS_COLOR[status] ?? 'gray'}>{enumLabel(t, status)}</Badge>;
};

export const UrgencyBadge: React.FC<{ urgency: NonNullable<Triage['urgency']> }> = ({ urgency }) => {
  const { t } = useTranslation();
  return <Badge color={URGENCY_COLOR[urgency] ?? 'gray'}>{enumLabel(t, urgency)}</Badge>;
};

export const fmtDate = (iso: string | null | undefined): string => formatDateTime(iso ?? undefined);

/** "1 500.00 EUR"-style money from the wire's decimal string. */
export const fmtMoney = (amount: string, currency: string): string => {
  const n = Number(amount);
  const value = Number.isFinite(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : amount;
  return `${value} ${currency}`;
};

/** A short, human title for a lead: its triaged issue type, else a generic label. */
export const leadTitle = (t: TFunction, lead: Lead): string =>
  lead.triage ? t('{{type}} request', { type: enumLabel(t, lead.triage.issue_type) }) : t('Request');

/**
 * What a proposal's amount means per fee basis - it becomes the matter's
 * pricing on accept (fixed/milestone → fixed fee + budget, capped → budget,
 * hourly → the lawyer's rate on the matter).
 */
export const amountLabel = (t: TFunction, basis: FeeBasis): string =>
  ({
    fixed: t('Fixed fee'),
    milestone: t('Total fee'),
    capped: t('Fee cap'),
    hourly: t('Hourly rate'),
  })[basis];

/** "15,000.00 NOK", or "2,400.00 NOK / hour" for an hourly rate. */
export const fmtProposalPrice = (t: TFunction, p: Pick<Proposal, 'fee_basis' | 'amount' | 'currency'>): string =>
  p.fee_basis === 'hourly'
    ? t('{{price}} / hour', { price: fmtMoney(p.amount, p.currency) })
    : fmtMoney(p.amount, p.currency);
